import api from './api';

// A department or area of a company (osasto / alue) with its visiting address
export interface IContactBranch {
  city: string;
  country: string;
  id: number;
  // Toimipiste, e.g. Tampere
  name: string;
  // Toimialue, e.g. Pirkanmaa
  operatingArea: string;
  postalNumber: string;
  streetAddress: string;
}

// The searches of the columns of the contact list (backend ContactColumnFilters); empty
// ones are left out
export interface IContactColumnFilters {
  businessId?: string;
  city?: string;
  companyName?: string;
  // yyyy-mm-dd: checked on or after the day
  creditCheckedFrom?: string;
  creditRating?: string;
  personCount?: string;
  postalNumber?: string;
  streetAddress?: string;
}

// The searches of the columns of the persons tab; office "all" finds those common to all areas
export interface IContactPersonSearch {
  companyName?: string;
  // The persons of one company
  contactId?: number;
  email?: string;
  firstName?: string;
  jobDescription?: string;
  lastName?: string;
  office?: string;
  operatingArea?: string;
  phone?: string;
  title?: string;
}

export interface IContactPersonRow {
  companyName: string;
  contactId: number;
  email: null | string;
  firstName: null | string;
  id: number;
  jobDescription: null | string;
  lastName: null | string;
  // Toimipiste, null when common to all the areas
  office: null | string;
  operatingArea: null | string;
  phone: null | string;
  phoneWithCountryCode: null | string;
  title: null | string;
}

export default {
  branches: {
    get: async (contactId: number | string): Promise<IContactBranch[]> =>
      api.get(`/api/v1/contacts/${contactId}/branches`),
    post: async (
      contactId: number | string,
      branch: Omit<IContactBranch, 'id'>
    ): Promise<{ id: number }> =>
      api.post(`/api/v1/contacts/${contactId}/branches`, branch),
    put: async (branch: IContactBranch): Promise<{ id: number }> =>
      api.put(`/api/v1/contacts/branches/${branch.id}`, branch),
    remove: async (id: number): Promise<{ id: number }> =>
      api.delete(`/api/v1/contacts/branches/${id}`),
  },
  delete: async (id: string): Promise<string> =>
    api.delete(`/api/v1/contacts/${id}`),
  get: async (
    filters: {
      limit?: number;
      offset?: number;
      ownOrganizationUnit?: boolean;
      search?: string;
      // Choosing a company for something new: the locked ones are left out
      selectable?: boolean;
      type?: string;
    },
    columnFilters?: IContactColumnFilters
  ): Promise<any> =>
    api.get('/api/v1/contacts', { ...filters, ...columnFilters }),
  count: async (
    search: string,
    type: string,
    columnFilters?: IContactColumnFilters
  ): Promise<any> =>
    api.get('/api/v1/contacts/count', { search, type, ...columnFilters }),
  // The contacts of the list with its filters as an Excel file, the texts in the language of
  // the user
  export: async (request: {
    filters?: IContactColumnFilters;
    search: string;
    texts: {
      companiesSheet: string;
      companyColumns: string[];
      fileName: string;
      personColumns: string[];
      personsSheet: string;
      types: Record<string, string>;
    };
    type: string;
  }): Promise<{ data: Blob; headers: Record<string, string> }> =>
    api.postForFile('/api/v1/contacts/export', request),
  getContact: async (
    contactId: string,
    queryParams?: { ownOrganizationUnit: boolean }
  ): Promise<any> => api.get(`/api/v1/contacts/${contactId}`, queryParams),
  getPerson: async (personId: string): Promise<any> =>
    api.get(`/api/v1/contacts/persons/${personId}`),
  getPersons: async (filters: {
    limit?: number;
    offset?: number;
    ownOrganizationUnit?: boolean;
  }): Promise<string[]> => api.get('/api/v1/contacts/persons', filters),
  getTypes: async (): Promise<string[]> => api.get('/api/v1/contacts/types'),
  // The persons of all the companies with the searches of the columns, a page at a time
  searchPersons: async (
    params: IContactPersonSearch & {
      limit: number;
      offset: number;
      ownOrganizationUnit?: boolean;
    }
  ): Promise<{ count: number; rows: IContactPersonRow[] }> =>
    api.get('/api/v1/contacts/persons/search', params),
  post: async (contact: any): Promise<any> =>
    api.post('/api/v1/contacts', contact),
  postPerson: async (contactId: string, person: any): Promise<any> =>
    api.post(`/api/v1/contacts/${contactId}/persons`, person),
  putContact: async (contactId: string, contact: any): Promise<string> =>
    api.put(`/api/v1/contacts/${contactId}`, contact),
  putPerson: async (personId: string, person: any): Promise<string> =>
    api.put(`/api/v1/contacts/persons/${personId}`, person),
  deleteContact: async (contactId: string): Promise<any> =>
    api.delete(`/api/v1/contacts/${contactId}`),
  // A company in use is locked instead: not chosen for new offers, projects or products
  patchLocked: async (contactId: string, locked: boolean): Promise<any> =>
    api.patch(`/api/v1/contacts/${contactId}/locked`, { locked }),
  deletePerson: async (personId: string): Promise<string> =>
    api.delete(`/api/v1/contacts/persons/${personId}`),
};
